import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import { Check, Plus } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const personalNoteDotColors = [
  "hsl(0 72% 51%)",
  "rgb(234 179 8)",
  "rgb(34 197 94)",
  "hsl(150 10% 46%)",
];

const PersonalNotesCard = ({ notes = [], onAdd, onRemove }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [newText, setNewText] = useState("");
  const [completingId, setCompletingId] = useState(null);
  const [removingIds, setRemovingIds] = useState(new Set());

  // Clean up removingIds once the parent actually removes the note (to avoid animation glitches)
  useEffect(() => {
    setRemovingIds((prev) => {
      const noteIds = new Set(notes.map((n) => n.id));
      const next = new Set([...prev].filter((id) => noteIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [notes]);

  // Notes minus the ones being animated out
  const visibleNotes = useMemo(
    () => notes.filter((n) => !removingIds.has(n.id)),
    [notes, removingIds],
  );

  const submitReminder = useCallback(() => {
    if (!newText.trim()) {
      setShowAdd(false);
      setNewText("");
      return;
    }
    onAdd?.(newText.trim());
    setNewText("");
    setShowAdd(false);
  }, [newText, onAdd]);

  const handleDone = useCallback(
    (id) => {
      setCompletingId(id);
      // Phase 1: checkmark bounce + strike-through (600ms)
      // Phase 2: optimistically hide → AnimatePresence plays exit, then call API
      setTimeout(() => {
        setCompletingId(null);
        setRemovingIds((prev) => new Set(prev).add(id));
        onRemove?.(id);
      }, 600);
    },
    [onRemove],
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "0.75rem",
        border: "1px solid hsl(140 8% 88%)",
        overflow: "hidden",
        backgroundColor: "#ffffff",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: "1px solid hsl(140 8% 88%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: "hsl(150 30% 12%)",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          }}
        >
          Personal Notes & Reminders
        </Typography>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 20,
            height: 20,
            px: "6px",
            borderRadius: "calc(-2px + 0.75rem)",
            backgroundColor: "hsl(217 91% 95%)",
            color: "hsl(217 70% 30%)",
            fontSize: 12,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            lineHeight: 1,
          }}
        >
          {notes.length}
        </Box>
      </Box>

      {notes.length === 0 ? (
        <Typography
          sx={{
            py: 4,
            textAlign: "center",
            fontSize: 14,
            color: "hsl(150 38% 17%)",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          All caught up!
          <Check size={16} color="hsl(142 71% 45%)" />
        </Typography>
      ) : (
        <AnimatePresence initial={false}>
          {visibleNotes.map((note, idx) => {
            const isCompleting = completingId === note.id;
            return (
              <motion.div
                key={note.id}
                initial={false}
                animate={{ opacity: 1, x: 0, height: "auto" }}
                exit={{ opacity: 0, x: 20, height: 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    borderTop: idx > 0 ? "1px solid hsl(140 8% 88%)" : "none",
                    overflow: "hidden",
                    "&:hover .done-circle": {
                      borderColor: "hsl(150 10% 46%)",
                    },
                    "&:hover .done-check": {
                      opacity: 0.4,
                    },
                    "&:hover": {
                      backgroundColor: "hsla(140, 8%, 90%, 0.3)",
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      mt: "6px",
                      flexShrink: 0,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: personalNoteDotColors[idx % personalNoteDotColors.length],
                      transition: "opacity 300ms",
                      opacity: isCompleting ? 0.3 : 1,
                    }}
                  />

                  <Typography
                    sx={{
                      flex: 1,
                      color: isCompleting
                        ? "hsl(150 10% 46%)"
                        : "hsl(150 30% 12%)",
                      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                      textDecoration: isCompleting ? "line-through" : "none",
                      textDecorationColor: isCompleting
                        ? "hsl(150 38% 17%)"
                        : undefined,
                      transition: "all 300ms",
                      fontSize: 14,
                      lineHeight: "1.25rem",
                    }}
                  >
                    {note.content}
                  </Typography>

                  <Box
                    component="button"
                    type="button"
                    className="done-circle"
                    onClick={() => !isCompleting && handleDone(note.id)}
                    sx={{
                      mt: "2px",
                      flexShrink: 0,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      border: "2px solid",
                      borderColor: isCompleting
                        ? "hsl(150 38% 17%)"
                        : "hsl(140 8% 88%)",
                      backgroundColor: isCompleting
                        ? "hsl(150 38% 17%)"
                        : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      padding: 0,
                      transition: "all 200ms",
                      transform: isCompleting ? "scale(1.1)" : "scale(1)",
                      animation: isCompleting
                        ? "tick-bounce 0.3s ease-out"
                        : "none",
                      "&:hover": isCompleting
                        ? {}
                        : {
                          borderColor: "hsl(150 38% 17%)",
                          backgroundColor: "hsla(150, 38%, 17%, 0.1)",
                        },
                    }}
                    title="Mark as done"
                  >
                    <Box
                      component="span"
                      className="done-check"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: isCompleting
                          ? "hsl(80, 97%, 72%)"
                          : "hsl(150, 38%, 17%)",
                        opacity: isCompleting ? 1 : 0,
                        transition: "all 200ms",
                      }}
                    >
                      <Check size={12} strokeWidth={3} />
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            );
          })}
        </AnimatePresence>
      )}

      {showAdd ? (
        <Box sx={{ px: 2.5, py: 1.5, borderTop: "1px solid hsl(140 8% 88%)" }}>
          <Box
            component="input"
            type="text"
            autoFocus
            value={newText}
            placeholder="e.g. Call back Soleil at 2:00 PM"
            onChange={(e) => setNewText(e.target.value)}
            onBlur={submitReminder}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitReminder();
              }
              if (e.key === "Escape") {
                setShowAdd(false);
                setNewText("");
              }
            }}
            sx={{
              display: "flex",
              height: "auto",
              minHeight: "0px",
              width: "100%",
              borderRadius: "10px",
              border: "1px solid hsl(140 8% 88%)",
              backgroundColor: "hsl(140 10% 95%)",       
              px: "12px",
              py: "8px",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              color: "hsl(150 30% 12%)",                 
              outline: "none",
              boxSizing: "border-box",
              transition: "box-shadow 0.15s, border-color 0.15s",
              "&::placeholder": {
                color: "hsl(150 10% 46%)",
              },
              "&:focus": {
                boxShadow: "0 0 0 2px hsl(140 10% 95%), 0 0 0 4px hsl(150 38% 17%)",
              },
            }}
          />
        </Box>
      ) : (
        <Button
          fullWidth
          disableElevation
          disableRipple
          onClick={() => setShowAdd(true)}
          sx={{
            justifyContent: "flex-start",
            borderRadius: 0,
            px: 2.5,
            py: 1.5,
            textTransform: "none",
            fontSize: 14,
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            color: "hsl(150 10% 46%)",
            borderTop: "1px solid hsl(140 8% 88%)",
            display: "flex",
            alignItems: "center",
            gap: 1,
            "&:hover": {
              backgroundColor: "hsla(140, 8%, 90%, 0.3)",
              color: "hsl(150 30% 12%)",
            },
          }}
        >
          <Plus size={16} />
          Add personal note
        </Button>
      )}
    </Paper>
  );
};

export default PersonalNotesCard;
